class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      if (this.input.form) {
        this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      }
      this.input.addEventListener(
        'input',
        theme.utils.debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}
if (!customElements.get('search-form')) customElements.define('search-form', SearchForm);


/* Relevant catalogue search. Product cards and product data are rendered by Liquid. */
(function () {
  'use strict';

  const STOP_WORDS = new Set([
    'a', 'as', 'o', 'os', 'um', 'uma', 'uns', 'umas', 'de', 'da', 'do', 'das',
    'dos', 'e', 'em', 'no', 'na', 'nos', 'nas', 'para', 'por', 'com', 'ao', 'aos',
    'the', 'an', 'and', 'or', 'of', 'for', 'with', 'to', 'in', 'on', 'at',
  ]);

  function normalize(value) {
    return String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&(?:nbsp|amp|quot|apos|lt|gt|#39|#160);/gi, ' ')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function words(value) {
    const normalized = normalize(value);
    return normalized ? normalized.split(' ') : [];
  }

  function queryTokens(query) {
    return [...new Set(words(query).filter((word) => word.length >= 2 && !STOP_WORDS.has(word)))];
  }

  // Only allow a single edit, or adjacent transposition, after four identical letters.
  function isSmallTypo(query, candidate) {
    if (query.length < 5 || candidate.length < 5 || query.slice(0, 4) !== candidate.slice(0, 4)) return false;
    if (Math.abs(query.length - candidate.length) > 1) return false;
    if (query === candidate) return true;
    if (query.length === candidate.length) {
      const differences = [];
      for (let index = 4; index < query.length; index += 1) {
        if (query[index] !== candidate[index]) differences.push(index);
        if (differences.length > 2) return false;
      }
      return differences.length === 1 || (differences.length === 2
        && differences[1] === differences[0] + 1
        && query[differences[0]] === candidate[differences[1]]
        && query[differences[1]] === candidate[differences[0]]);
    }
    const shorter = query.length < candidate.length ? query : candidate;
    const longer = query.length < candidate.length ? candidate : query;
    let shortIndex = 4;
    let longIndex = 4;
    let skipped = false;
    while (shortIndex < shorter.length && longIndex < longer.length) {
      if (shorter[shortIndex] === longer[longIndex]) {
        shortIndex += 1;
        longIndex += 1;
      } else {
        if (skipped) return false;
        skipped = true;
        longIndex += 1;
      }
    }
    return true;
  }

  function matchWord(query, candidate) {
    if (query === candidate) return 3;
    if (query.length >= 2 && candidate.startsWith(query)) return 2;
    return isSmallTypo(query, candidate) ? 1 : 0;
  }

  function prepareRecord(record, index) {
    const titleWords = words(record.title);
    return {
      ...record,
      index,
      normalizedTitle: normalize(record.title),
      titleWords,
      phraseWords: titleWords.filter((word) => !STOP_WORDS.has(word)),
      secondaryWords: [...new Set(words([
        record.type, record.vendor, ...record.tags, ...record.collections,
      ].join(' ')))],
      bodyWords: [...new Set(words(record.body))],
    };
  }

  function bestWordMatch(token, candidates, weights) {
    let score = 0;
    for (const candidate of candidates) {
      score = Math.max(score, weights[matchWord(token, candidate)] || 0);
      if (score === weights[3]) break;
    }
    return score;
  }

  function hasPrefixPhrase(titleWords, tokens) {
    for (let start = 0; start <= titleWords.length - tokens.length; start += 1) {
      if (tokens.every((token, index) => matchWord(token, titleWords[start + index]) >= 2)) return true;
    }
    return false;
  }

  function scoreRecord(record, query, tokens = queryTokens(query)) {
    if (!String(query || '').trim()) return 0;
    if (!tokens.length) return -1;
    let score = 0;
    // Each meaningful query word must match. Description-only matches rank below names.
    for (const token of tokens) {
      const match = Math.max(
        bestWordMatch(token, record.titleWords, [0, 60, 100, 130]),
        bestWordMatch(token, record.secondaryWords, [0, 15, 35, 45]),
        bestWordMatch(token, record.bodyWords, [0, 8, 18, 25]),
      );
      if (!match) return -1;
      score += match;
    }
    if (record.normalizedTitle === normalize(query)) score += 1000;
    const phrase = tokens.join(' ');
    if ((' ' + record.phraseWords.join(' ') + ' ').includes(' ' + phrase + ' ')) score += 500;
    else if (hasPrefixPhrase(record.phraseWords, tokens)) score += 300;
    return score;
  }

  // Expose only the pure helpers to local Node verification, without browser globals.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { normalize, queryTokens, isSmallTypo, matchWord, prepareRecord, scoreRecord };
  }
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('heybeni-search-results')) return;

  class HeybeniSearchResults extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.query = this.dataset.query || '';
      this.list = this.querySelector('[data-results]');
      this.count = this.querySelector('[data-count]');
      this.error = this.querySelector('[data-error]');
      this.empty = this.querySelector('[data-empty]');
      this.shown = this.querySelector('[data-shown]');
      this.more = this.querySelector('[data-more]');
      this.reset = this.querySelector('[data-reset]');
      this.availability = this.querySelector('[data-availability]');
      this.maxPrice = this.querySelector('[data-max-price]');
      this.sort = this.querySelector('[data-sort]');
      this.pageSize = this.querySelector('[data-page-size]');
      this.defaultPageSize = 16;
      this.limit = 16;
      this.priceLimit = null;
      this.hidden = true;
      this.loadCatalogue(this.controller);
    }

    disconnectedCallback() {
      if (!this.ready && this.controller) {
        this.controller.abort();
        this.initialized = false;
      }
    }

    positiveInteger(value, maximum = Number.MAX_SAFE_INTEGER) {
      const parsed = Number(value);
      if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > maximum) throw new Error('Invalid catalogue pagination');
      return parsed;
    }

    readPage(component, expectedPage, expectedPages) {
      const current = this.positiveInteger(component.dataset.page);
      const pages = this.positiveInteger(component.dataset.pages);
      if (current > pages || current !== expectedPage || pages !== expectedPages
        || component.dataset.section !== this.dataset.section || component.dataset.query !== this.dataset.query
        || component.dataset.pageParam !== this.dataset.pageParam) throw new Error('Inconsistent catalogue page');
      const script = component.querySelector('script[data-catalogue]');
      const list = component.querySelector('[data-results]');
      if (!script || !list) throw new Error('Missing catalogue data');
      const records = JSON.parse(script.textContent);
      if (!Array.isArray(records) || records.length > 250 || (!records.length && pages !== 1)) throw new Error('Invalid catalogue records');
      const cards = new Map();
      for (const card of Array.from(list.children)) {
        if (!card.matches('li[data-product-id]')) throw new Error('Invalid product card');
        const id = card.dataset.productId;
        if (!id || cards.has(id)) throw new Error('Duplicate product card');
        cards.set(id, card);
      }
      const ids = new Set();
      const validated = records.map((record) => {
        if (!record || !['string', 'number'].includes(typeof record.id)) throw new Error('Invalid product ID');
        const id = String(record.id);
        if (!/^\d+$/.test(id) || ids.has(id) || !cards.has(id)) throw new Error('Inconsistent product card');
        ids.add(id);
        for (const field of ['title', 'body', 'type', 'vendor']) {
          if (typeof record[field] !== 'string') throw new Error('Invalid product text');
        }
        for (const field of ['tags', 'collections']) {
          if (!Array.isArray(record[field]) || record[field].some((value) => typeof value !== 'string')) throw new Error('Invalid product metadata');
        }
        if (typeof record.available !== 'boolean' || !Number.isSafeInteger(record.price) || record.price < 0) throw new Error('Invalid product filters');
        return { record: { ...record, id }, card: cards.get(id) };
      });
      if (ids.size !== cards.size) throw new Error('Unmatched product cards');
      return validated;
    }

    async fetchPage(page, pages, controller) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set(this.dataset.pageParam, String(page));
      const response = await fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Catalogue request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const component = Array.from(document.querySelectorAll('heybeni-search-results'))
        .find((element) => element.dataset.section === this.dataset.section);
      if (!component) throw new Error('Missing catalogue section');
      return this.readPage(component, page, pages).map(({ record, card }) => ({
        record,
        card: window.document.importNode(card, true),
      }));
    }

    async loadCatalogue(controller) {
      try {
        if (!this.list || !this.count || !this.error || !this.empty || !this.shown || !this.more
          || !this.reset || !this.availability || !this.maxPrice || !this.sort || !this.pageSize
          || !this.dataset.section) throw new Error('Missing search interface');
        this.defaultPageSize = this.positiveInteger(this.dataset.pageSize || '16', 32);
        if (![16, 24, 32].includes(this.defaultPageSize)) throw new Error('Invalid search page size');
        this.limit = this.defaultPageSize;
        const current = this.positiveInteger(this.dataset.page);
        const pages = this.positiveInteger(this.dataset.pages);
        if (pages > 1 && !this.dataset.pageParam) throw new Error('Missing catalogue page parameter');
        const pageResults = new Map([[current, this.readPage(this, current, pages)]]);
        const missing = Array.from({ length: pages }, (_, index) => index + 1).filter((page) => page !== current);
        // Fetch complete catalogue pages in small batches before replacing native results.
        for (let offset = 0; offset < missing.length; offset += 3) {
          const batch = missing.slice(offset, offset + 3);
          const results = await Promise.all(batch.map((page) => this.fetchPage(page, pages, controller)));
          results.forEach((result, index) => pageResults.set(batch[index], result));
        }
        if (controller.signal.aborted || controller !== this.controller || !this.isConnected) return;
        const ids = new Set();
        this.records = [];
        this.cards = new Map();
        for (let page = 1; page <= pages; page += 1) {
          for (const { record, card } of pageResults.get(page)) {
            if (ids.has(record.id)) continue;
            ids.add(record.id);
            this.records.push(prepareRecord(record, this.records.length));
            this.cards.set(record.id, card);
          }
        }
        const tokens = queryTokens(this.query);
        this.matches = this.records.map((record) => ({ record, score: scoreRecord(record, this.query, tokens) }))
          .filter((result) => result.score >= 0);
        this.bindControls();
        this.render();
        this.ready = true;
        this.dataset.state = 'ready';
        const section = this.closest('[id^="shopify-section-"]') || this.parentElement;
        const nativeResults = section && section.querySelector('.collection-wrapper[data-native-search-results]');
        if (nativeResults) {
          const contentList = this.querySelector('[data-content-results]');
          const contentArea = this.querySelector('[data-content-area]');
          const contentCards = Array.from(nativeResults.querySelectorAll('li[data-result-type="page"], li[data-result-type="article"]'));
          if (contentList && contentArea && contentCards.length) {
            contentList.replaceChildren(...contentCards);
            contentArea.hidden = false;
          }
          nativeResults.before(this);
          nativeResults.remove();
        }
        const nativeCount = section && section.querySelector('.template-search__header p[role="status"]');
        if (nativeCount) nativeCount.hidden = true;
        this.hidden = false;
      } catch (error) {
        if (controller.signal.aborted || controller !== this.controller || !this.isConnected) return;
        this.dataset.state = 'error';
        if (this.error) {
          // Keep the native fallback visible, showing only the error from this interface.
          this.prepend(this.error);
          for (const child of this.children) child.hidden = child !== this.error;
          this.error.textContent = 'Não foi possível carregar o filtro de pesquisa. Pode continuar a usar os resultados abaixo.';
          this.error.setAttribute('role', 'alert');
          this.error.hidden = false;
          this.hidden = false;
        }
        console.warn('Heybeni: não foi possível carregar o catálogo completo da pesquisa.');
      }
    }

    bindControls() {
      const options = { signal: this.controller.signal };
      this.count.setAttribute('role', 'status');
      this.count.setAttribute('aria-live', 'polite');
      this.more.type = 'button';
      this.reset.type = 'button';
      this.pageSize.value = String(this.defaultPageSize);
      for (const control of [this.availability, this.sort, this.pageSize]) {
        control.addEventListener('change', () => {
          this.limit = this.selectedPageSize();
          this.render();
        }, options);
      }
      this.maxPrice.addEventListener('input', () => {
        this.limit = this.selectedPageSize();
        this.render();
      }, options);
      this.more.addEventListener('click', (event) => {
        event.preventDefault();
        this.limit += this.selectedPageSize();
        this.render();
      }, options);
      this.reset.addEventListener('click', (event) => {
        event.preventDefault();
        this.availability.value = 'all';
        this.maxPrice.value = '';
        this.sort.value = 'relevance';
        this.pageSize.value = String(this.defaultPageSize);
        this.limit = this.defaultPageSize;
        this.render();
      }, options);
    }

    selectedPageSize() {
      const size = Number(this.pageSize.value);
      return [16, 24, 32].includes(size) ? size : this.defaultPageSize;
    }

    render() {
      const rawPrice = this.maxPrice.value.trim();
      const amount = rawPrice === '' ? null : Number(rawPrice);
      const invalidPrice = this.maxPrice.validity.badInput || (amount !== null && (!Number.isFinite(amount) || amount < 0));
      this.maxPrice.setCustomValidity(invalidPrice ? 'Introduza um preço igual ou superior a 0.' : '');
      this.maxPrice.setAttribute('aria-invalid', String(invalidPrice));
      this.error.textContent = invalidPrice ? 'Introduza um preço igual ou superior a 0.' : '';
      this.error.hidden = !invalidPrice;
      if (!invalidPrice) this.priceLimit = amount === null ? null : Math.round(amount * 100);
      const results = this.matches.filter(({ record }) => (
        (this.availability.value !== 'in-stock' || record.available)
        && (this.priceLimit === null || record.price <= this.priceLimit)
      ));
      const collator = new Intl.Collator('pt-PT', { sensitivity: 'base', numeric: true });
      results.sort((left, right) => {
        const title = collator.compare(left.record.title, right.record.title);
        switch (this.sort.value) {
          case 'price-ascending': return left.record.price - right.record.price || title || left.record.index - right.record.index;
          case 'price-descending': return right.record.price - left.record.price || title || left.record.index - right.record.index;
          case 'title-ascending': return title || left.record.index - right.record.index;
          case 'title-descending': return -title || left.record.index - right.record.index;
          default: return right.score - left.score || left.record.index - right.record.index;
        }
      });
      const visible = results.slice(0, this.limit);
      this.list.replaceChildren(...visible.map(({ record }) => this.cards.get(record.id)));
      this.list.hidden = results.length === 0;
      this.empty.hidden = results.length !== 0;
      const productWord = results.length === 1 ? 'produto encontrado' : 'produtos encontrados';
      this.count.textContent = `${results.length} ${productWord}${this.query.trim() ? ` para “${this.query}”` : ''}.`;
      this.shown.textContent = visible.length ? `A mostrar 1–${visible.length} de ${results.length} produtos.` : '0 de 0 produtos.';
      this.more.hidden = visible.length >= results.length;
      this.more.disabled = this.more.hidden;
    }
  }

  window.customElements.define('heybeni-search-results', HeybeniSearchResults);
})();
